import type { Experiment, Result } from "@growthbook/growthbook";
import { GrowthbookClientProvider } from "@openfeature/growthbook-client-provider";
import { FirstSuccessfulStrategy, WebMultiProvider } from "@openfeature/multi-provider-web";
import {
  type EvaluationContext,
  OpenFeature,
  OpenFeatureProvider,
  useFlag,
} from "@openfeature/react-sdk";
import React, { ReactNode } from "react";

import type { Models } from "@/api/types";
import { useTranslation } from "@/core/i18n";
import { Analytics } from "modules/analytics";

/**
 * Feature flags and their "default" values that are currently available/tracked
 * in the admin web app.
 *
 * The English literal copy on the Adonis CTA/banner text flags below (and on
 * `beautyBankTerm`) mirrors the default variant configured in GrowthBook and
 * exists here for typing (`Flags`), the `typeof`-based dispatch in
 * `getFeatureFlag`, and as the ultimate fallback default passed to
 * `useFlag`/`OpenFeature`. It is never rendered to a user directly: the
 * `useFeatureFlag` hook resolves a localized default for these keys via
 * `getLocalizedFlagDefault` before handing a default value to `useFlag`, so
 * patients see copy in their own language whenever GrowthBook doesn't
 * override it. See `src/services/translations/`.
 */
const featureFlags = {
  /**
   * Assume that this flag is always "on" in dev environments, and always "off"
   * in production
   */
  "isDev": true,
  /**
   * Tablet/desktop layout: top navigation, multi-column pages, site footer.
   * Off renders the original single-column phone layout at every width.
   */
  "desktopExperience": false,
  "unitedDerm": false,
  "patientSupportChat": false,
  /**
   * Gates the arrival-reminder location permission flow and the native geofence identify. Off by
   * default: the primer must not appear until a store build containing the geofence plugin has
   * reached patients (see AppVersionCheck).
   */
  "rmdLocationTriggeredCheckin": false,
  /**
   * Renders the arrival-reminder primer in a browser so its copy and flow can be reviewed on a
   * snapshot environment, where no native build exists and the OS prompts cannot run. Preview only:
   * accepting shows the next stage instead of asking the OS for anything.
   */
  "rmdLocationPrimerPreview": false,
  /**
   * Rollout gate for practice-managed Meta and Google ad pixels. Off by default and while unresolved:
   * no App Tracking Transparency prompt and no vendor script loads for any practice until it is
   * enabled, so the web release can ship ahead of the native app that carries the tracking plugin.
   */
  "adPixels": false,

  "adonisHomeCTAText": {
    header: "Find the Right Treatment with {Name}",
    subheader: "{Pronoun}'s the answer to all your questions.",
    chat_box: "What are your goals?",
    chat_submit: "Ask {Name}",
  },

  "adonisV3CTAText": {
    header: "Hey {username}, want to discover your next favorite treatment?",
    subheader: "",
    chat_box: "Start typing to chat",
    chat_submit: "Ask {Name}",
    link_text: "View all recommendations",
  },

  "adonisRecommandationCarouselText": {
    header: "Recommended for {username}...",
    link: "View your personalized recommendations",
    chat_box: "Start typing to chat",
    chat_submit: "Ask {Name}",
  },

  "adonisShopCategoryLinkText": {
    title: "Just for {username}",
    subtitle: "Recommended for you by {Name}",
    badge_text: "NEW PICKS",
  },

  "adonisShopCategoryHeaderText": {
    title: "Just for you, {username}",
    subtitle: "Curated for you by {Name} AI",
  },

  /*
   * Controls whether to show the "Flexible Membership Terms" feature
   */
  "flexible_membership_terms": false,
  /**
   * Controls whether to show membership reward selection after checkout
   */
  "selectMembershipRewardsAfterCheckout": true,

  /**
   * Dynamic text for Adonis CTA banner
   */
  "adonisDynamicCTAText": {
    line1: "Find the Right",
    line2: "Treatment with {Name}",
  },
  "beautyBankTerm": "Beauty Bank",
  /**
   * Controls whether MyProducts shipping features are visible (shipping labels, PDP info, fulfillment selection, cart grouping)
   */
  "myProductsShipping": false,
  "plg-discounts": false,
  /**
   * Smoke test for GrowthBook experiments: every page reads it and nothing depends on its value, so a
   * dev-only experiment on it shows assignment and the exposure without borrowing a real flag.
   */
  "patient-smoke-e2e": false,
};

// Mapped type makes the IDE annotations a bit nicer
type Flags = { [K in keyof typeof featureFlags]: (typeof featureFlags)[K] };

// Override default values from environment variable for local development
import.meta.env.VITE_ENABLED_FEATURE_FLAGS?.split(",").forEach((flag) => {
  if (typeof featureFlags[flag as keyof Flags] === "boolean") {
    (featureFlags[flag as keyof Flags] as unknown) = true;
  } else {
    console.warn(`Unknown feature flag '${flag}' specified in VITE_ENABLED_FEATURE_FLAGS`);
  }
});

/**
 * The exposure GrowthBook's Segment warehouse query reads once Segment snake-cases the keys
 * (`experimentId` becomes `experiment_id`).
 */
export const experimentViewedProperties = (
  experiment: Experiment<unknown>,
  result: Result<unknown>,
) => ({
  experimentId: experiment.key,
  variationId: result.key,
  hashAttribute: result.hashAttribute,
  hashValue: String(result.hashValue),
});

// ProviderEntryInput is not re-exported from the package root and its internal
// type path is not exposed via package exports (unreachable under
// moduleResolution: bundler), so derive it from the public constructor signature.
type ProviderEntryInput = ConstructorParameters<typeof WebMultiProvider>[0][number];

const providers: ProviderEntryInput[] = [];

if (import.meta.env.VITE_GROWTHBOOK_CLIENT_ID && !import.meta.env.VITEST) {
  providers.push({
    name: "growthbook",
    provider: new GrowthbookClientProvider(
      {
        clientKey: import.meta.env.VITE_GROWTHBOOK_CLIENT_ID,
        enableDevMode: import.meta.env.DEV,
        trackingCallback: (experiment, result) => {
          Analytics.track("Experiment Viewed", experimentViewedProperties(experiment, result));
        },
      },
      {
        streaming: true,
        timeout: 2000, // important that this is less than the `clientReadyPromise` timeout
      },
    ),
  });
} else {
  console.warn(
    "VITE_GROWTHBOOK_CLIENT_ID variable not provided, feature flags will revert to default values",
  );
}

const clientReady = OpenFeature.setProviderAndWait(
  new WebMultiProvider(providers, new FirstSuccessfulStrategy()),
);
const client = OpenFeature.getClient();

export const FFlagProvider = (props: { children: ReactNode }) => {
  return <OpenFeatureProvider client={client}>{props.children}</OpenFeatureProvider>;
};

/**
 * Resolves a localized default for the text-bearing feature flags (Adonis
 * CTA/banner copy variants and `beautyBankTerm`) so `useFeatureFlag` can hand
 * `useFlag` a default value in the patient's current language instead of the
 * hardcoded English literal in `featureFlags`. Returns `undefined` for every
 * other flag, so the caller falls back to `featureFlags[key]` unchanged.
 *
 * `{Name}` / `{Pronoun}` / `{username}`-style tokens are a separate
 * placeholder mechanism resolved later by `replacePersonaPlaceholders` (see
 * `modules/adonis/adonis.helpers.ts`) and must be preserved literally by every
 * translation.
 */
const getLocalizedFlagDefault = <Flag extends keyof Flags>(
  key: Flag,
  t: ReturnType<typeof useTranslation>["t"],
): Flags[Flag] | undefined => {
  switch (key) {
    case "adonisHomeCTAText":
      return {
        header: t("flags.adonisHomeCTAText.header"),
        subheader: t("flags.adonisHomeCTAText.subheader"),
        chat_box: t("flags.adonisHomeCTAText.chatBox"),
        chat_submit: t("flags.adonisHomeCTAText.chatSubmit"),
      } as Flags[Flag];
    case "adonisV3CTAText":
      return {
        header: t("flags.adonisV3CTAText.header"),
        // Intentionally blank in every locale: no subheader is shown for this variant.
        subheader: "",
        chat_box: t("flags.adonisV3CTAText.chatBox"),
        chat_submit: t("flags.adonisV3CTAText.chatSubmit"),
        link_text: t("flags.adonisV3CTAText.linkText"),
      } as Flags[Flag];
    case "adonisRecommandationCarouselText":
      return {
        header: t("flags.adonisRecommandationCarouselText.header"),
        link: t("flags.adonisRecommandationCarouselText.link"),
        chat_box: t("flags.adonisRecommandationCarouselText.chatBox"),
        chat_submit: t("flags.adonisRecommandationCarouselText.chatSubmit"),
      } as Flags[Flag];
    case "adonisShopCategoryLinkText":
      return {
        title: t("flags.adonisShopCategoryLinkText.title"),
        subtitle: t("flags.adonisShopCategoryLinkText.subtitle"),
        badge_text: t("flags.adonisShopCategoryLinkText.badgeText"),
      } as Flags[Flag];
    case "adonisShopCategoryHeaderText":
      return {
        title: t("flags.adonisShopCategoryHeaderText.title"),
        subtitle: t("flags.adonisShopCategoryHeaderText.subtitle"),
      } as Flags[Flag];
    case "adonisDynamicCTAText":
      return {
        line1: t("flags.adonisDynamicCTAText.line1"),
        line2: t("flags.adonisDynamicCTAText.line2"),
      } as Flags[Flag];
    case "beautyBankTerm":
      return t("flags.beautyBankTerm") as Flags[Flag];
    default:
      return undefined;
  }
};

/**
 * Lower-level hooks API to subscribe to the value of a particular feature flag.
 */
export const useFeatureFlag = <Flag extends keyof Flags>(key: Flag) => {
  const { t } = useTranslation();
  const defaultValue = getLocalizedFlagDefault(key, t) ?? featureFlags[key];
  const { value } = useFlag<Flags[Flag]>(key as unknown as string, defaultValue);
  return value;
};

/**
 * Lower-level API to get the current value of a particular feature flag.
 */
export const getFeatureFlag = async <Flag extends keyof Flags>(key: Flag) => {
  await clientReady;
  const flagKey = key as unknown as string;
  switch (typeof featureFlags[key]) {
    case "boolean":
      return client.getBooleanValue(flagKey, featureFlags[key]) as unknown as Flags[Flag];
    case "number":
      return client.getNumberValue(flagKey, featureFlags[key]) as unknown as Flags[Flag];
    case "string":
      return client.getStringValue(flagKey, featureFlags[key]) as unknown as Flags[Flag];
    default:
      return client.getObjectValue(flagKey, featureFlags[key]) as Flags[Flag];
  }
};

/**
 * Wrapper component that will render its children only if a feature flag
 * matches the specified value
 *
 * If the `equals` prop is not specified, the children will be rendered if the
 * flag is truthy. Otherwise, they will be rendered only if the flag matches the
 * specified value.
 *
 * @example
 *
 * const MyComponent = () => {
 *   return (
 *     <>
 *       <IfFlag flag="isDev">
 *         <div>This will only be rendered in dev environments</div>
 *       </IfFlag>
 *
 *       <IfFlag flag="isDev" equals={false}>
 *         <div>This will only be rendered in production environments</div>
 *       </IfFlag>
 *    </>
 *   );
 * };
 */
export const IfFlag = <Flag extends keyof Flags>(props: {
  flag: Flag;
  equals?: Flags[Flag];
  children: React.ReactNode;
}) => {
  const flagValue = useFeatureFlag(props.flag);
  if (props.equals === undefined ? flagValue : flagValue === props.equals)
    return <>{props.children}</>;
  else return null;
};

/**
 * Returns the passed object conditionally based on the value of a feature flag.
 *
 * If `equals` argument is not specified, the object will be returned if the
 * flag is truthy. Otherwise, it will be returned only if it matches the
 * specified value.
 *
 * Don't use this in React components, use `<IfFlag />` or `useFeatureFlag()`
 * instead.
 *
 * @example
 *
 * const loader = () => {
 *   const environmentData = await ifFlag("isDev")
 *     ? getForDevelopment()
 *     : getForProduction();
 *
 *   return json(environmentData);
 * };
 */
export const ifFlag =
  <Flag extends keyof typeof featureFlags>(flag: Flag, equals?: (typeof featureFlags)[Flag]) =>
  async <T,>(obj: T): Promise<T | null> => {
    const flagValue = await getFeatureFlag(flag);
    if (equals === undefined ? flagValue : flagValue === equals) return obj;
    else return null;
  };

/**
 * GrowthBook hashes an experiment on a top-level attribute and never resolves a dotted path such as
 * `user.targetingKey`, so an experiment's hash attribute in GrowthBook must be one of these names.
 * They are snake_case to match the warehouse identifier types that experiment analysis joins on.
 */
export const USER_ID_ATTRIBUTE = "user_id";
export const PLATFORM_ID_ATTRIBUTE = "platform_id";

type UserContext = {
  id: string;
  name?: string;
  email?: string;
};

type PlatformContext = {
  id: string;
  domain?: string;
  created: Date;
};

export const buildFlagUserContext = (
  user: UserContext,
  platform?: PlatformContext | null,
): EvaluationContext => ({
  appHost: globalThis.location?.hostname ?? "",
  [USER_ID_ATTRIBUTE]: user.id,
  ...(platform && { [PLATFORM_ID_ATTRIBUTE]: platform.id }),
  user: {
    targetingKey: user.id,
    name: user.name ?? null,
    email: user.email ?? null,
  },
  platform: platform
    ? {
        targetingKey: platform.id,
        domain: platform.domain ?? null,
        created: platform.created.toISOString(),
      }
    : null,
});

export const buildFlagPlatformContext = (platform: { id: string } | null): EvaluationContext => ({
  ...(platform && { [PLATFORM_ID_ATTRIBUTE]: platform.id }),
  platform: platform ? { targetingKey: platform.id } : null,
});

/**
 * Identify the current user context for feature flagging
 */
export const identifyFlagUser = (user: UserContext, platform?: PlatformContext | null) => {
  return OpenFeature.setContext(buildFlagUserContext(user, platform));
};

/**
 * Identify a platform-only context for surfaces that render without a signed-in user
 */
export const identifyFlagPlatform = (platform: { id: string } | null) => {
  return OpenFeature.setContext(buildFlagPlatformContext(platform));
};

/**
 * Seed the practice into the flag context when nothing has identified it yet, so a flag targeted
 * per practice (such as `desktopExperience`) resolves for the practice from the first render
 * rather than flashing its global value. A context that already carries this practice, such as a
 * signed-in user's, is left alone.
 */
export const seedFlagPlatform = async (platform: { id: string }) => {
  if (OpenFeature.getContext()[PLATFORM_ID_ATTRIBUTE] === platform.id) return;
  await identifyFlagPlatform(platform);
};

type SignedInPatient = Pick<
  Models<"UserModel1">,
  "id" | "fullName" | "email" | "platform" | "platformDomain"
>;

/**
 * Identify the flag context on a page that renders with or without a signed-in patient. A signed-in
 * patient keeps their user attributes there, so a user-split experiment does not drop them.
 */
export const identifyFlagVisitor = (
  patient: SignedInPatient | null,
  platform: { id: string } | null,
) => {
  if (!patient) {
    return identifyFlagPlatform(platform);
  }

  return identifyFlagUser(
    { id: patient.id, name: patient.fullName ?? undefined, email: patient.email ?? undefined },
    patient.platform && {
      id: patient.platform.id,
      domain: patient.platformDomain?.domain ?? undefined,
      created: new Date(patient.platform.created),
    },
  );
};
